import { batchActions } from 'redux-batched-actions';
import { call, put, select } from 'redux-saga/effects';
import { updateArtboard } from 'containers/Artboards/actions';
import { activeArtboard } from 'containers/Artboards/selectors';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { CombinedLayoutsList, LayoutMap } from 'models';
import { handleSagaError } from 'services/handleError';
import { setLayout } from '../actions';
import * as Models from '../models';
import { orderedActiveLayoutIds, orderedActiveLayoutsBySectionId } from '../selectors';
import { CAROUSEL_ITEM_TYPES } from './addCarousel';

function updateOtherSlidesNumbers(
  allCarouselSlides: CombinedLayoutsList,
  sourceSlideId: string,
  sourceSlideNumber: number,
  targetSlidePosition: number,
): LayoutMap[] {
  const layoutsToUpdate: LayoutMap[] = [];

  allCarouselSlides.forEach((layout) => {
    const carouselItem = (layout as LayoutMap).get('carouselItem');
    const currentSlideId = carouselItem?.get('slideId');
    const currentSlideNumber = carouselItem?.get('slideNumber') ?? 0;

    if (currentSlideId === sourceSlideId) {
      return;
    }

    let newSlideNumber = currentSlideNumber;

    // If the slide is moved forward (sourceSlideNumber < targetSlidePosition)
    if (sourceSlideNumber < targetSlidePosition) {
      // Shift all slides between the source and target positions back by one position
      if (currentSlideNumber > sourceSlideNumber && currentSlideNumber <= targetSlidePosition) {
        newSlideNumber = currentSlideNumber - 1;
      }
    } else {
      // If the slide is moved backward (sourceSlideNumber > targetSlidePosition)
      // Shift all slides between the target and source positions forward by one position
      if (currentSlideNumber >= targetSlidePosition && currentSlideNumber < sourceSlideNumber) {
        newSlideNumber = currentSlideNumber + 1;
      }
    }
    if (newSlideNumber !== currentSlideNumber) {
      const updatedCarouselItem = carouselItem?.set('slideNumber', newSlideNumber);
      const updatedLayout = (layout as LayoutMap).set('carouselItem', updatedCarouselItem);
      layoutsToUpdate.push(updatedLayout);
    }
  });

  return layoutsToUpdate;
}

function findInsertionIndex(
  sortedCarouselSlides: CombinedLayoutsList,
  updatedCarouselLayoutIds: ReturnType<typeof orderedActiveLayoutIds>,
  sourceSlideId: string,
  targetSlidePosition: number,
): number {
  let insertionIndex = 0;

  for (let i = 0; i < sortedCarouselSlides.size; i++) {
    const slideLayout = sortedCarouselSlides.get(i) as LayoutMap;
    const carouselItem = slideLayout.get('carouselItem');
    const slideNumber = carouselItem?.get('slideNumber') ?? 0;
    const slideId = carouselItem?.get('slideId');

    // Skip the source slide since we're moving it
    if (slideId === sourceSlideId) {
      continue;
    }

    if (slideNumber < targetSlidePosition) {
      const layoutId = slideLayout.get('id');
      const layoutIdIndex = updatedCarouselLayoutIds.indexOf(layoutId);
      if (layoutIdIndex !== -1) {
        insertionIndex = layoutIdIndex + 1;
      }
    } else {
      break;
    }
  }

  return insertionIndex;
}

function removeLayoutsFromOrder(
  layoutIds: ReturnType<typeof orderedActiveLayoutIds>,
  layoutIdsToRemove: string[],
): ReturnType<typeof orderedActiveLayoutIds> {
  let updatedLayoutIds = layoutIds;

  layoutIdsToRemove.forEach((layoutId) => {
    const currentIndex = updatedLayoutIds.indexOf(layoutId);
    if (currentIndex !== -1) {
      updatedLayoutIds = updatedLayoutIds.delete(currentIndex);
    }
  });

  return updatedLayoutIds;
}

function insertLayoutsAtPosition(
  layoutIds: ReturnType<typeof orderedActiveLayoutIds>,
  layoutIdsToInsert: string[],
  insertionIndex: number,
): ReturnType<typeof orderedActiveLayoutIds> {
  let updatedLayoutIds = layoutIds;

  layoutIdsToInsert.forEach((layoutId, index) => {
    updatedLayoutIds = updatedLayoutIds.insert(insertionIndex + index, layoutId);
  });

  return updatedLayoutIds;
}

function updateLayoutIdsOrder(
  layoutIds: ReturnType<typeof orderedActiveLayoutIds>,
  allCarouselSlides: CombinedLayoutsList,
  sourceSlideLayouts: CombinedLayoutsList,
  sourceSlideId: string,
  targetSlidePosition: number,
): ReturnType<typeof orderedActiveLayoutIds> {
  // Get all layout IDs from carousel slides (only slide layouts, not other carousel items)
  const allCarouselSlideLayoutIds = allCarouselSlides.map(layout => (layout as LayoutMap).get('id')).toArray();
  const sourceSlideLayoutIds = sourceSlideLayouts.map(layout => (layout as LayoutMap).get('id')).toArray();

  // Create a working copy with only carousel slide layouts
  const carouselLayoutIds = layoutIds.filter(layoutId => allCarouselSlideLayoutIds.includes(layoutId));

  // Remove source slide layouts from their current positions within carousel layouts
  const updatedCarouselLayoutIds = removeLayoutsFromOrder(carouselLayoutIds, sourceSlideLayoutIds);

  // Get all carousel slides sorted by slide number
  const sortedCarouselSlides = allCarouselSlides.sort((a, b) => {
    const slideNumberA = ((a as LayoutMap).get('carouselItem')?.get('slideNumber') ?? 0);
    const slideNumberB = ((b as LayoutMap).get('carouselItem')?.get('slideNumber') ?? 0);

    return slideNumberA - slideNumberB;
  });

  // Find the insertion position for the moved slide
  const insertionIndex = findInsertionIndex(
    sortedCarouselSlides,
    updatedCarouselLayoutIds,
    sourceSlideId,
    targetSlidePosition,
  );

  // Insert source slide layouts at the calculated position
  const reorderedCarouselLayoutIds = insertLayoutsAtPosition(
    updatedCarouselLayoutIds,
    sourceSlideLayoutIds,
    insertionIndex,
  );

  // Merge the updated carousel layout order back into the original layoutIds
  let updatedLayoutIds = removeLayoutsFromOrder(layoutIds, allCarouselSlideLayoutIds);

  // Find the position to insert the reordered carousel layouts
  let insertionIndexInOriginal = 0;
  for (let i = 0; i < layoutIds.size; i++) {
    const layoutId = layoutIds.get(i);
    if (layoutId && allCarouselSlideLayoutIds.includes(layoutId)) {
      insertionIndexInOriginal = i;
      break;
    }
  }

  // Insert all reordered carousel layouts at the found position
  updatedLayoutIds = insertLayoutsAtPosition(
    updatedLayoutIds,
    reorderedCarouselLayoutIds.toArray(),
    insertionIndexInOriginal,
  );

  return updatedLayoutIds;
}

export function* changeCarouselSlidePosition(action: Models.Action.IChangeCarouselSlidePosition): Generator {
  try {
    const { sourceSlide, targetSlidePosition } = action.payload;
    const { carouselId, slideId: sourceSlideId, slideNumber: sourceSlideNumber, sectionId } = sourceSlide;
    const layoutsBySectionId: ReturnType<typeof orderedActiveLayoutsBySectionId> = yield select(orderedActiveLayoutsBySectionId);
    const artboard: ReturnType<typeof activeArtboard> = yield select(activeArtboard);
    const layoutIds: ReturnType<typeof orderedActiveLayoutIds> = yield select(orderedActiveLayoutIds);
    const sectionLayouts = layoutsBySectionId.get(sectionId);
    const allCarouselSlides = sectionLayouts?.filter((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');

      return carouselItem?.get('carouselId') === carouselId &&
        carouselItem?.get('type') === CAROUSEL_ITEM_TYPES.SLIDE;
    });

    if (!allCarouselSlides || allCarouselSlides.size === 0) {
      return;
    }

    const sourceSlideLayouts = allCarouselSlides.filter((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');

      return carouselItem?.get('slideId') === sourceSlideId;
    });

    if (!sourceSlideLayouts || sourceSlideLayouts.size === 0) {
      return;
    }

    if (sourceSlideNumber === targetSlidePosition) {
      return;
    }

    const layoutsToUpdate: LayoutMap[] = [];

    sourceSlideLayouts.forEach((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');
      const updatedCarouselItem = carouselItem?.set('slideNumber', targetSlidePosition);
      const updatedLayout = (layout as LayoutMap).set('carouselItem', updatedCarouselItem);
      layoutsToUpdate.push(updatedLayout);
    });

    const otherSlidesUpdates = updateOtherSlidesNumbers(
      allCarouselSlides,
      sourceSlideId,
      sourceSlideNumber,
      targetSlidePosition,
    );

    layoutsToUpdate.push(...otherSlidesUpdates);

    if (layoutsToUpdate.length === 0) {
      return;
    }

    // Update layoutIds order in artboard to reflect new slide positions
    const updatedLayoutIds = updateLayoutIdsOrder(
      layoutIds,
      allCarouselSlides,
      sourceSlideLayouts,
      sourceSlideId,
      targetSlidePosition,
    );

    const updatedArtboard = artboard.set('layoutIds', updatedLayoutIds);

    yield put(batchActions([
      saveAppState(),
      ...layoutsToUpdate.map(layout => setLayout(layout)),
      updateArtboard(updatedArtboard),
    ]));
  } catch (error) {
    yield call(handleSagaError, error, 'Layouts.changeCarouselSlidePosition', 'ChangeCarouselSlidePosition');
  }
}
